import styled from "@emotion/styled";
import { useCallback } from "react";
import { Typography, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAdminResetUserPassword } from "@queries/users/useAdminResetUserPassword";
import { toast } from "@shared/services/toast";
import { copyToClipboard } from "@shared/utils/copy-to-clipboard";
import { blurActiveElement } from "@shared/utils/blur-active-element";
import { useUIStore } from "@store/ui.store";

const PasswordText = styled.code`
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  background-color: ${(p) => p.theme.colors.background3};
`;

type Params = {
  email: string;
};

export const useAdminResetPasswordAction = () => {
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const mutation = useAdminResetUserPassword({
    onSuccess: async (data, email) => {
      const copyPassword = () => {
        copyToClipboard(data.password);
        toast.success("Password copied to clipboard.", {
          toastId: "copy-password",
        });
      };
      await confirmDialog({
        title: "New password",
        content: (
          <>
            <Typography>
              <strong>User email:</strong> {email}
            </Typography>
            <Typography>
              <strong>New password:</strong>{" "}
              <PasswordText>{data.password}</PasswordText>{" "}
              <IconButton
                onClick={copyPassword}
                aria-label="Copy temporary password"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Typography>
            <Typography mt={1} variant="body2">
              On first sign-in, the user will be requested to change their
              password.
            </Typography>
          </>
        ),
        confirmButtonText: "Close",
        cancelButtonText: false,
      });
    },
  });

  const resetPassword = useCallback(
    async ({ email }: Params) => {
      if (!email) return;

      const answer = await confirmDialog({
        title: "Reset password?",
        content: (
          <>
            <Typography>{email}</Typography>
            <Typography mt={2} mb={1}>
              A new temporary password will be generated for this user, and
              their current password will stop working immediately.
              You&apos;ll be able to copy the new password and send it to
              them.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This won&apos;t work if the user signs in exclusively through
              SSO, since they don&apos;t have a password to reset.
            </Typography>
          </>
        ),
        confirmButtonText: "Reset password",
        cancelButtonText: "Cancel",
      });

      blurActiveElement();

      if (!answer) return;

      mutation.mutate({ email });
    },
    [confirmDialog, mutation]
  );

  return { resetPassword, isPending: mutation.isPending };
};
